import { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MenuFoldOutlined, MenuUnfoldOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar/Navbar';
import { ProfileButton } from './components';
import { ITEM_DATA } from '@/components/Navbar/Navbar.constants';
import { ALLOWED_PATHS, FORECASTING_GROUP_PATHS } from '@/constants/paths';
import { PRIVATE_PATH } from '@/constants';
import { NAMESPACE } from '@/constants';
import { useThemeMode } from '@/styles';
import { LayoutProps } from './Layout.types';
import {
  BottomNavLink,
  ProfileButtonWrap,
  SiderBottomSection,
  StyledContainer,
  StyledLayout,
  StyledSider,
  SiderTop,
  SkipLink,
  ThemeToggle
} from './Layout.styles';
import { Logo } from './components';

/**
 * Sidebar widths, shared with the content margin.
 *
 * The content sits beside a fixed sider, so it has to reserve exactly the
 * sider's width. Kept as constants because the two were previously separate
 * literals that had to be remembered together.
 */
const SIDER_WIDTH = 232;
const SIDER_COLLAPSED_WIDTH = 80;

const SIDER_COLLAPSED_STORAGE_KEY = 'nrve-sider-collapsed';

/**
 * Someone who opens or folds the menu keeps that choice. Until they do, it
 * starts folded on the Sales Dashboard pages, which need the width.
 */
const initialCollapsed = (pathname: string): boolean => {
  try {
    const stored = window.localStorage.getItem(SIDER_COLLAPSED_STORAGE_KEY);
    if (stored != null) return stored === 'true';
  } catch {
    /* Private window or blocked storage: fall back to the page default. */
  }
  return FORECASTING_GROUP_PATHS.some((path) => pathname.startsWith(path));
};

const Layout: FC<LayoutProps> = ({ children, user }) => {
  const displayName = user?.displayName ?? '';
  const photoURL = user?.photoURL;
  const role = user?.role;
  const { t } = useTranslation(NAMESPACE.COMMON);
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => initialCollapsed(pathname));
  const { mode, toggleMode } = useThemeMode();
  /* Names the mode the button switches to, not the one in use: the sun shows
     in dark mode, the moon in light. */
  const themeToggleLabel = t(mode === 'dark' ? 'lightMode' : 'darkMode');

  const allowedPaths = role != null ? ALLOWED_PATHS[role as keyof typeof ALLOWED_PATHS] ?? [] : [];
  const showTraderControls = allowedPaths.includes(PRIVATE_PATH.TRADER_CONTROLS);
  const showUserSettings = allowedPaths.includes(PRIVATE_PATH.USER_SETTINGS);
  const userSettingsItem = ITEM_DATA[PRIVATE_PATH.USER_SETTINGS];
  const traderControlsItem = ITEM_DATA[PRIVATE_PATH.TRADER_CONTROLS];
  const isUserSettingsActive = pathname?.startsWith(PRIVATE_PATH.USER_SETTINGS) ?? false;
  const isTraderControlsActive = pathname?.startsWith(PRIVATE_PATH.TRADER_CONTROLS) ?? false;

  return (
    <StyledLayout hasSider>
      <SkipLink href="#main-content">{t('skipToContent')}</SkipLink>
      <StyledSider
        collapsible
        breakpoint="md"
        collapsedWidth={SIDER_COLLAPSED_WIDTH}
        width={SIDER_WIDTH}
        collapsed={collapsed}
        onCollapse={(value, type) => {
          /* The breakpoint also reports "wide enough" on load; that means the
             menu may open, not that it must, so it goes back to the remembered
             or page default. Folding on a narrow window still applies. */
          if (type === 'responsive') {
            setCollapsed(value || initialCollapsed(pathname));
            return;
          }
          setCollapsed(value);
          try {
            window.localStorage.setItem(SIDER_COLLAPSED_STORAGE_KEY, String(value));
          } catch {
            /* The choice just won't persist. */
          }
        }}
        trigger={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      >
        <SiderTop $collapsed={collapsed}>
          <Logo collapsed={collapsed} />
          <ThemeToggle
            type="button"
            onClick={toggleMode}
            $collapsed={collapsed}
            aria-label={themeToggleLabel}
            title={themeToggleLabel}
          >
            {mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
          </ThemeToggle>
        </SiderTop>
        <Navbar role={role} collapsed={collapsed} />
        <SiderBottomSection>
          {showTraderControls && traderControlsItem && (
            <BottomNavLink
              to={PRIVATE_PATH.TRADER_CONTROLS}
              $active={isTraderControlsActive}
              $collapsed={collapsed}
              aria-current={isTraderControlsActive ? 'page' : undefined}
              /* Collapsed the label is gone, so the link needs its name from aria-label. */
              aria-label={collapsed ? t(traderControlsItem.i18nKey) : undefined}
              title={collapsed ? t(traderControlsItem.i18nKey) : undefined}
            >
              <span className="anticon" aria-hidden>
                {traderControlsItem.icon}
              </span>
              {!collapsed && <span>{t(traderControlsItem.i18nKey)}</span>}
            </BottomNavLink>
          )}
          {showUserSettings && userSettingsItem && (
            <BottomNavLink
              to={PRIVATE_PATH.USER_SETTINGS}
              $active={isUserSettingsActive}
              $collapsed={collapsed}
              aria-current={isUserSettingsActive ? 'page' : undefined}
              aria-label={collapsed ? t(userSettingsItem.i18nKey) : undefined}
              title={collapsed ? t(userSettingsItem.i18nKey) : undefined}
            >
              <span className="anticon" aria-hidden>
                {userSettingsItem.icon}
              </span>
              {!collapsed && <span>{t(userSettingsItem.i18nKey)}</span>}
            </BottomNavLink>
          )}
          <ProfileButtonWrap $collapsed={collapsed}>
            <ProfileButton url={photoURL} name={displayName} />
          </ProfileButtonWrap>
        </SiderBottomSection>
      </StyledSider>
      <StyledContainer
        id="main-content"
        style={{ marginLeft: collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH }}
      >
        {children}
      </StyledContainer>
    </StyledLayout>
  );
};

export default Layout;
